import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedDialog,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	FieldLayout,
	LabelPosition,
	RHookFormTextFieldWithLabel,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { Box, Button, Grid, styled, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
	GET_QUERY_KEY,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	QUERY_OPTIONS,
	ROWS_PER_PAGE_PER_OPTIONS,
	STATE_SETUP_SCREEN_SLUG,
} from "../../common/constants";
import { IGetListRequest, IResponse, IServerError } from "../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { CountryService } from "../../services/common/CountryService";
import { DDLDataTable } from "../DDLDataTable";
import { IAppState } from "../../store/reducers";
import { IState } from "../../models";
import { StateService } from "../../services/StateService";
import { StateSetupToolbar } from "./StateSetupToolbar";
import { StateSetupValidationSchema } from "./validator";
import { setSelectedActivityLogActions } from "../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions/stickyNavBarActions";
import { useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IStateSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY: any = "state-setup-single-data-record";
const STATE_LIST_QUERY_KEY: any = "State-list";

const useStates = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["State-list", params],
		queryFn: () => StateService.getState(params, navigateToLogout),
		refetchOnWindowFocus: false,
	});
const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => StateService.getById(id, navigateToLogout),
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: () => CountryService.getCountryDDL(),
		refetchOnWindowFocus: false,
	});

export const base64ToFile = (
	base64String: string,
	fileName: string,
	mimeType: string
): FileList => {
	// Helper function to create a File object from base64 string
	const base64ToFile = (
		base64String: string,
		fileName: string,
		mimeType: string
	): File => {
		const byteString = atob(base64String);
		const byteArray = new Uint8Array(byteString.length);

		for (let i = 0; i < byteString.length; i++) {
			byteArray[i] = byteString.charCodeAt(i);
		}

		return new File([byteArray], fileName, { type: mimeType });
	};

	// Create the File object
	const file = base64ToFile(base64String, fileName, mimeType);

	// Creating a FileList using DataTransfer
	const dataTransfer = new DataTransfer();
	dataTransfer.items.add(file);

	return dataTransfer.files;
};

export const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

export const convertImageToBase64 = (file: any) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			if (reader.result) {
				resolve(reader.result as string); // Return Base64 string
			} else {
				reject(new Error("Failed to convert file to Base64"));
			}
		};
		reader.onerror = () => {
			reject(new Error("Error reading file"));
		};
		reader.readAsDataURL(file);
	});
};

export const StateSetup: React.FC<IStateSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const theme = useTheme();
	const isPendingActivityLogData = useSelector(
		(state: IAppState) => state.isLoadAxiosCall.isLoading
	);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	// const useGetCountryDDL = () => {
	// 	return useQuery(GET_QUERY_KEY.COUNTRY_DDL, () => CountryService.get(), {
	// 	  ...QUERY_OPTIONS,
	// 	});
	//   };
	//   const { data: countryList } =
	//   useGetCountryDDL();

	const accountName = useSelector(
		(state: IAppState) => state.accountant.AccountantName
	);
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [stateId, setStateId] = useState<any>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const { isPending: isSubmitting, mutate: submitFormFields } = useMutation({
		mutationFn: StateService.create,

		onSuccess: (res: IStateSetup | IServerError | IResponse | any) => {


			if (res.status === 201) {
				setEditRecordId(res.id);
				queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				let path = `/state/setup/${res.data.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [STATE_LIST_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else if (res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				let path = `/state/setup/${res.data.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [STATE_LIST_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);
	const { isPending: isPendingCountryDDL, data: countryList } =
		useGetCountryDDL();


	const { isPending, data } = useStates(queryParams, navigateToLogout);
	const stateList: any = data;

	const upadtedStateListItems = useMemo(() => {
		return (
			stateList &&
			stateList.items &&
			stateList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/state/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.name}
					</Link>
				),
			}))
		);
	}, [stateList]);

	const DEFAULT_VALUES = {
		name: undefined,
		shortCode: undefined,
		country: undefined,
	};
	const formMethods = useForm<any>({
		resolver: yupResolver(StateSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = async (values: any) => {


		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			name: values.name,
			shortCode: values.shortCode,
			country: values.country,
		};

		submitFormFields(payload);
	};

	useEffect(() => {

		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				// @ts-ignore
				const fieldValue = selectedDataRecord[fieldName];
				// @ts-ignore
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "logo" && fieldValue !== "") {
					const file = base64ToFile(fieldValue, "image.png", "image/png");
					formMethods.setValue(fieldName, file);
					setLogoName("uploaded logo");
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteStateSetupById } = useMutation({
		mutationFn: StateService.delState,
		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the StateSetup fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/state/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [STATE_LIST_QUERY_KEY],
			});
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteStateSetupField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				stateId: stateId,
				navigateToLogout: navigateToLogout,
			};
			deleteStateSetupById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setStateId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setStateId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveStateById } = useMutation({
		mutationFn: StateService.archiveState,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the State fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/state/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [STATE_LIST_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveStateField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				stateId: stateId,
				navigateToLogout: navigateToLogout,
			};
			archiveStateById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setStateId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: IState | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/state/setup/${params.value}`;
		navigate(path);
	}, []);

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/state/setup`;
		navigate(path);
	};

	const expandedLabels = ["Misc.", "State"];
	const expandedStateSetupItem = "State";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Misc." && item.label === expandedStateSetupItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedStateSetupItem && "/state/setup",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: id ? id : "",
				entityId2: "",
				entityId3: "",
				screenName: STATE_SETUP_SCREEN_SLUG,
				companyName: accountName,
				activityType:
					"activity_type=create&activity_type=updated&activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);
	const [logoName, setLogoName] = useState<string>("");
	const onLogoSelectionChangeHandler = async (event: any) => {
		const file = event.target.files?.[0];
		if (file) {
			setLogoName(file.name);
		}
	};

	return (
		<Grid
		// sx={{ pb: "5rem", "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingById ||
					isPendingCountryDDL ||
					isDeleting ||
					isPendingActivityLogData ||
					isArchive
				}
				testId="accountant Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteStateSetupField}
				cancelActionHandler={onCloseHandler}
				testId="accountant-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="State"
										testId={`State`}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<RHookFormTextFieldWithLabel
											label="Name"
											id="name"
											testId={`State-name`}
											labelPosition={LabelPosition.Top}
											required
										/>
										<RHookFormTextFieldWithLabel
											label="Short Code"
											id="shortCode"
											testId={`State-shortCode`}
											labelPosition={LabelPosition.Top}
											required
										/>
										<EnhancedDropDownRHFWithLabel
											selectName="country"
											label="Country"
											labelPosition={LabelPosition.Top}
											required
											testId={`country-list`}
											items={countryList ?? []}
											labelColumns={4}
											fieldColumns={8}
										/>
									</StandardPanel>
								}
								toolbar={
									<StateSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								testId="form-layout"
								showFullDiaplay={true}
								toolbarPosition="bottom"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5.4, lg: 5.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3.1, lg: 3.1, xl: 3.1 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									stateList && upadtedStateListItems
										? upadtedStateListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									stateList && stateList.total ? stateList.total : 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveStateField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"accountant-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
